import { type KeyboardEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibField } from '@chewy/kib-fields-new-react'
import { KibGridItem } from '@chewy/kib-layout-react'
import classNames from 'classnames'
import { useFields } from '@pbt/pbt-ui-components'

import type { EditableTitleProps } from './EditableTitle'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import styles from './KibEditableTitle.module.scss'

const KibEditableTitle = ({
  title,
  setTitle,
  readOnly,
  endAdornment,
}: EditableTitleProps) => {
  const { t } = useTranslation(['Common'])
  const [titleEditing, setTitleEditing] = useState(false)

  const {
    fields: { title: titleField },
    validate,
  } = useFields([
    {
      name: 'title',
      validators: ['required'],
      initialValue: title,
    },
  ])

  const handleStartEditing = () => {
    if (!titleEditing) {
      setTitleEditing(true)
    }
  }

  const handleFinishEditing = () => {
    if (titleEditing) {
      const isDirty = title !== titleField.value
      if (validate() && isDirty) {
        setTitle(titleField.value)
      } else {
        titleField.setValue(title)
      }
      setTitleEditing(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && titleEditing) {
      handleFinishEditing()
    }
    if (e.key === 'Enter' && !titleEditing) {
      handleStartEditing()
    }
    if (e.key === 'Escape') {
      titleField.setValue(title)
      setTitleEditing(false)
    }
  }

  useEffect(() => {
    titleField.setValue(title)
  }, [title])

  const isReadOnly = readOnly || !titleEditing

  return (
    <>
      <KibGridItem className={styles.titleWrapper}>
        {isReadOnly && (
          <div className={styles.titleHeader}>{t('Common:MESSAGE')}:</div>
        )}
        <KibField
          className={classNames({ [styles.readOnly]: isReadOnly })}
          disabled={readOnly}
          hiddenLabel={isReadOnly}
          label={t('Common:SUBJECT')}
          maxLength={255}
          readOnly={isReadOnly}
          value={titleField.value}
          onBlur={handleFinishEditing}
          onChange={titleField.set}
          onClick={handleStartEditing}
          onFocus={handleStartEditing}
          onKeyDown={handleKeyDown}
        />
      </KibGridItem>
      {endAdornment}
    </>
  )
}

export default KibEditableTitle
